import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import React, { useEffect, useMemo } from 'react';
import { cdnImage } from '@/utils/image-utils';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';
import { selectIsThailand } from '@/store/selectors/commonSelectors';
import { envIsSweden } from '@/constants/constants';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import {
  selectPromotionsError,
  selectPromotionsInProgress,
  selectPromotionWidgets,
} from '@/store/cms/promotions/selectors';
import PromotionsLoading from '@/pages/promotions/PromotionsLoading';
import PromotionsOverview from '@/pages/promotions/PromotionsOverview';
import PromotionsError from '@/pages/promotions/PromotionsError';
import PromotionsEmpty from '@/pages/promotions/PromotionsEmpty';
import PromotionsRestrictedNotice from '@/pages/promotions/PromotionsRestrictedNotice';

const BG_DESKTOP = cdnImage('ld_promotions_bg_desktop.jpg', { width: '1900', height: '480' });
const BG_TABLET = cdnImage('ld_promotions_bg_tablet.png', {
  width: '1024',
  height: '320',
  fit: 'contain',
});
const BG_MOBILE = cdnImage('ld_promotions_bg_mobile.png', {
  width: '640',
  height: '320',
  fit: 'contain',
});

function PromotionsPage() {
  const intl = useIntl();
  const promotionsError = useSelector(selectPromotionsError);
  const promotionsLoading = useSelector(selectPromotionsInProgress);
  const promotions = useSelector(selectPromotionWidgets);
  const isRestricted = useRestrictedMode();
  const isThailand = useSelector(selectIsThailand);
  const navigate = useAbsoluteNavigate();

  // Only the restricted markets without promotions (Thailand, Sweden) redirect home; Alberta
  // (ROC) keeps its Alberta-specific notice. Non-restricted markets are unaffected.
  const redirectToHome = isRestricted && (isThailand || envIsSweden());

  useEffect(() => {
    if (redirectToHome) {
      navigate('/');
    }
  }, [redirectToHome, navigate]);

  const VisibleComponent = useMemo(() => {
    if (isRestricted) {
      return <PromotionsRestrictedNotice />;
    }

    if (promotionsLoading) {
      return <PromotionsLoading />;
    }

    if (promotionsError) {
      return <PromotionsError />;
    }

    if (promotions.length === 0) {
      return <PromotionsEmpty />;
    }

    return <PromotionsOverview promotions={promotions} />;
  }, [promotions, promotionsError, promotionsLoading, isRestricted]);

  if (redirectToHome) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>LuckyDays - {intl.formatMessage({ id: 'promotions.header' })}</title>
      </Helmet>
      <div className="pt-16 md:pt-32 pb-16 relative">
        <div className="absolute left-0 right-0 bottom-0 top-0 after:absolute after:right-0 after:left-0 after:bottom-0 after:top-0 after:bg-black/40 z-0">
          <img
            className="w-full h-full object-cover object-center"
            src={BG_DESKTOP}
            srcSet={`${BG_MOBILE} 640w, ${BG_TABLET} 1024w, ${BG_DESKTOP} 1440w`}
            alt={intl.formatMessage({ id: 'promotions.header' })}
          />
        </div>
        <div className="relative text-center">
          <h1 className="text-white mb-0">{intl.formatMessage({ id: 'promotions.header' })}</h1>
        </div>
      </div>
      <section className="max-w-5xl mx-auto px-4">{VisibleComponent}</section>
    </>
  );
}

export default PromotionsPage;
